import { type StepDef, type FieldDef, f, sel, inSection, tight, when, req, reqWhen, reqHint, withRule, hint, radio, ph, dob, pct, whole, nmax, mx, floorOf, checkOf } from "./model";
import { STATE_OPTS, TITLES, OPT } from "./constants";
import { PRICING_PROGRAMS, PRICING_RATE_FLOORS, IC_PER_TXN_FLOOR, AMEX_NOT_HIGHER_MESSAGE } from "./pricing";
import { FIELD_MAX } from "@/lib/fieldLimits";
import type { V2Bag } from "@/features/applications/api/applications.api";

const nonUsOwner = (b: V2Bag) => b.isUsCitizen === false;

const program = (...names: string[]) => (b: V2Bag) =>
  typeof b.pricingProgram === "string" && names.includes(b.pricingProgram);
const PRICING_MAX_RATE_PCT = 10;
const PRICING_MAX_FEE = 10;
const pctFor = (show: (b: V2Bag) => boolean, key: string, label: string) =>
  when(show, reqWhen(show, nmax(PRICING_MAX_RATE_PCT, f(key, label, "number"))));
const usdFor = (show: (b: V2Bag) => boolean, key: string, label: string) =>
  when(show, reqWhen(show, nmax(PRICING_MAX_FEE, f(key, label, "number"))));
const pinDebitOn = (b: V2Bag) => b.pinDebitEnabled === true;
const amexCustom = (b: V2Bag) => b.amexCustomRate === true;
const acceptsAch = (b: V2Bag) => b.acceptAch === true;
const achMissing = (otherKey: string) => (b: V2Bag) =>
  acceptsAch(b) && (b[otherKey] === null || b[otherKey] === undefined || b[otherKey] === "");

const secEq = (s: string) => (b: V2Bag) => b.patriotActSection === s;
const govtDoc = (d: string) => (b: V2Bag) => b.patriotActSection === "I" && b.patriotGovtDoc === d;
const bizDoc = (d: string) => (b: V2Bag) => b.patriotActSection === "II" && b.patriotBusinessDoc === d;
const indDoc = (d: string) => (b: V2Bag) => b.patriotActSection === "III" && b.patriotIndividualDoc === d;

export const STEPS: StepDef[] = [
  {
    n: 1,
    label: "Configuration",
    fields: [
      req(sel("leadSource", "Lead Source", OPT.leadSource)),
      { key: "includePatriotAct", label: "Patriot Act", type: "yesno", locked: true,
        hint: "Required on every BLogic application — locked Yes." },
      { key: "includeCardAssocDisclosure", label: "Card Association Disclosure Page", type: "yesno", locked: true,
        hint: "Required on every BLogic application — locked Yes." },
      { key: "includePetroFleetAddendum", label: "Petro / Fleet Merchant Addendum", type: "yesno", locked: true, lockedValue: false,
        hint: "Not available via the ELAPP API — locked No." },
      { key: "includeAchAddendum", label: "ACH Addendum", type: "yesno",
        hint: "Only required for TransIT with ACH, TXP with ACH, and TC Plus." },
      { key: "includeFdgLeasing", label: "FDG Leasing Agreement", type: "yesno", locked: true, lockedValue: false,
        hint: "Not available via the ELAPP API — locked No." },
      { key: "includeEsignature", label: "eSignature", type: "yesno", required: true },
    ],
  },
  {
    n: 2,
    label: "Business",
    fields: [
      ...inSection("Legal entity", [
        req(mx(FIELD_MAX.appWizard.legalName, f("legalName", "Legal Name of Business"))),
        req(mx(FIELD_MAX.default.address, f("legalAddress", "Legal Address"))),
        mx(FIELD_MAX.default.address, f("legalSuite", "Suite / PO Box")),
        withRule("zip", req(mx(FIELD_MAX.default.zip, f("legalZip", "Legal ZIP")))),
        req(mx(FIELD_MAX.default.city, f("legalCity", "Legal City"))),
        req(sel("legalState", "Legal State", STATE_OPTS)),
        withRule("phone", req(mx(FIELD_MAX.default.phone, f("legalPhone", "Legal Phone")))),
        withRule("phone", mx(FIELD_MAX.default.phone, f("legalFax", "Legal Fax"))),
      ]),
      ...inSection("DBA / location", [
        req(mx(FIELD_MAX.appWizard.dbaName, f("dbaName", "DBA Name"))),
        req(mx(FIELD_MAX.default.address, f("dbaAddress", "DBA Address"))),
        mx(FIELD_MAX.default.address, f("dbaSuite", "Suite")),
        withRule("zip", req(mx(FIELD_MAX.appWizard.dbaZip, f("dbaZip", "DBA ZIP")))),
        req(mx(FIELD_MAX.appWizard.dbaCity, f("dbaCity", "DBA City"))),
        req(sel("dbaState", "DBA State", STATE_OPTS)),
        withRule("phone", req(mx(FIELD_MAX.default.phone, f("dbaPhone", "DBA Phone")))),
        withRule("phone", mx(FIELD_MAX.default.phone, f("dbaFax", "DBA Fax"))),
      ]),
      ...inSection("Business details", [
        reqWhen(
          (b) => b.tsysBusinessType === "Internet"
            || (Array.isArray(b.advertisingMethods) && b.advertisingMethods.includes("Internet")),
          mx(FIELD_MAX.appWizard.website, f("website", "Website")),
        ),
        withRule("phone", req(mx(FIELD_MAX.default.phone, f("merchantCustomerServicePhone", "Merchant Customer Service Phone")))),
        hint("Years + months the merchant has owned the business.", req(nmax(999, whole(f("lengthOwnedYears", "Length Owned (years)", "number"))))),
        nmax(11, whole(f("lengthOwnedMonths", "Length Owned (months)", "number"))),
        mx(FIELD_MAX.appWizard.assocNumber, f("assocNumber", "Assoc #")),
        mx(FIELD_MAX.appWizard.salesRepCode, f("salesRepCode", "Sales Rep Code")),
        { key: "fleetIndicator", label: "Fleet Services", type: "yesno", locked: true, lockedValue: false,
          hint: "Not available via the ELAPP API — locked No." },
        { key: "fuelIndicator", label: "Fuel / Petro Support", type: "yesno", locked: true, lockedValue: false,
          hint: "Not available via the ELAPP API — locked No." },
      ]),
      ...inSection("Emails", [
        withRule("email", req(mx(FIELD_MAX.appWizard.email, f("email", "Contact Email")))),
        withRule("email", mx(FIELD_MAX.default.email, f("chargebackEmail", "Chargeback Email"))),
      ]),
      ...inSection("Contact", [
        req(sel("chargebackAddressPreference", "Chargebacks routed to", OPT.addr3)),
        req(sel("statementAddressPreference", "Statements routed to", OPT.addr4)),
        req(sel("contactTitle", "Contact Title", OPT.contactTitle)),
        withRule("phone", req(mx(FIELD_MAX.default.phone, f("contactPhone", "Contact Phone")))),
        withRule("lettersOnly", req(mx(FIELD_MAX.default.name, f("contactFirstName", "Contact First Name")))),
        withRule("lettersOnly", req(mx(FIELD_MAX.default.name, f("contactLastName", "Contact Last Name")))),
        f("contactExtension", "Contact Phone Ext."),
      ]),
      ...inSection("Previous bankruptcies", [
        req(f("hasBusinessBankruptcy", "Business bankruptcy?", "yesno")),
        when((b) => b.hasBusinessBankruptcy === true, f("businessBankruptcyDate", "Business Bankruptcy Date", "date")),
        req(f("hasPersonalBankruptcy", "Personal bankruptcy?", "yesno")),
        when((b) => b.hasPersonalBankruptcy === true, f("personalBankruptcyDate", "Personal Bankruptcy Date", "date")),
      ]),
      ...inSection("IRS / W-9", [
        req(f("receive1099KViaEmail", "Receive 1099-K via secure email?", "yesno")),
        when((b) => b.receive1099KViaEmail === true,
          reqWhen((b) => b.receive1099KViaEmail === true, f("irsPaperlessConsent", "Consent to paperless (electronic) delivery of IRS documents", "yesno"))),
        req(sel("ownershipType", "Type of Ownership", OPT.ownership)),
        f("exemptPayee", "Exempt Payee?", "yesno"),
        f("isTaxExempt", "501(c)(3) Tax exempt?", "yesno"),
        req(sel("taxIdType", "Type of Tax ID", OPT.taxIdType)),
        withRule("taxId", hint("Exact TIN from the last income tax return — 9 digits.", req(mx(FIELD_MAX.appWizard.taxId, f("taxId", "Tax ID #"))))),
        req(sel("tsysBusinessType", "Business Type", OPT.businessType)),
        withRule("mcc", hint("From the processor's catalog.", req(mx(FIELD_MAX.appWizard.mcc, f("mcc", "MCC / SIC"))))),
        hint("Name reported to the IRS, if different from the legal name.", mx(40, f("irsReportingName", "IRS Reporting Name"))),
        req(mx(FIELD_MAX.appWizard.productDescription, f("productDescription", "Description of Products or Services Sold", "textarea"))),
      ]),
    ],
  },
  {
    n: 3,
    label: "Owners",
    repeat: {
      collection: "principals",
      title: "Owner / Officer",
      noun: "owner",
      minRows: 1,
      rowFields: [
        ...inSection("Roles & FinCEN flags", [
          f("isBeneficialOwner", "Beneficial Owner", "checkbox"),
          f("isIndividualWithControl", "Individual with Control", "checkbox"),
          f("isSigningApp", "Signing App", "checkbox"),
          f("isPersonalGuarantor", "Personal Guarantor", "checkbox"),
          f("skipCreditPull", "Skip credit pull (2 yrs financials)", "checkbox"),
        ]),
        ...inSection("Identity", [
          withRule("lettersOnly", req(mx(FIELD_MAX.appPrincipal.firstName, f("firstName", "First Name")))),
          withRule("lettersOnly", mx(FIELD_MAX.appPrincipal.middleName, f("middleName", "Middle"))),
          withRule("lettersOnly", req(mx(FIELD_MAX.appPrincipal.lastName, f("lastName", "Last Name")))),
          mx(4, f("suffix", "Suffix")),
          req(sel("title", "Title", TITLES)),
          req(f("isUsCitizen", "U.S. Citizen / Resident?", "yesno")),
          mx(FIELD_MAX.appPrincipal.countryOfResidence, f("countryOfResidence", "Country of Residence / Issuance")),
          when((b) => b.isUsCitizen === false, mx(FIELD_MAX.appPrincipal.passportNumber, f("passportNumber", "Passport / Alien-ID Number"))),
          reqHint((b) => b.skipCreditPull !== true && !(b.isUsCitizen === false && !!b.passportNumber && !!b.countryOfResidence), withRule("ssn", ph("XXX-XX-XXXX", mx(FIELD_MAX.appPrincipal.ssn, f("ssn", "Social Security Number"))))),
          req(dob(f("dob", "Date of Birth", "date"))),
          req(pct(f("ownershipPercentage", "% Ownership", "number"))),
        ]),
        ...inSection("Home address", [
          req(mx(FIELD_MAX.appPrincipal.homeAddress, f("homeAddress", "Home Address"))),
          sel("streetDirection", "Street Direction", OPT.streetDirection),
          sel("streetType", "Street Type", OPT.streetType),
          mx(FIELD_MAX.appPrincipal.homeUnit, f("homeUnit", "Unit")),
          withRule("zip", ph("94103", req(mx(FIELD_MAX.appPrincipal.homeZip, f("homeZip", "ZIP"))))),
          req(mx(FIELD_MAX.appPrincipal.homeCity, f("homeCity", "City"))),
          req(sel("homeState", "State", STATE_OPTS)),
          withRule("phone", req(mx(FIELD_MAX.appPrincipal.homePhone, f("homePhone", "Residential Phone")))),
          withRule("email", req(mx(FIELD_MAX.appPrincipal.email, f("email", "Email")))),
        ]),
        ...inSection("Foreign address (non-US owners)", [
          when(nonUsOwner, reqWhen(nonUsOwner, mx(100, f("foreignAddress1", "Foreign Address 1")))),
          when(nonUsOwner, reqWhen(nonUsOwner, mx(100, f("foreignAddress2", "Foreign Address 2")))),
          when(nonUsOwner, reqWhen(nonUsOwner, mx(100, f("foreignAddress3", "Foreign Address 3")))),
          when(nonUsOwner, reqWhen(nonUsOwner, mx(100, f("foreignCity", "City / Town / Village")))),
          when(nonUsOwner, reqWhen(nonUsOwner, mx(100, f("foreignState", "State / Province / Region")))),
          when(nonUsOwner, reqWhen(nonUsOwner, mx(50, f("foreignZip", "Postal Code")))),
        ]),
      ],
    },
  },
  {
    n: 4,
    label: "Processing",
    fields: [
      ...inSection("Operations", [
        req(nmax(999, whole(f("numberOfLocations", "Number of Locations", "number")))),
        mx(16, f("affiliatedMid", "Existing/Affiliated Merchant ID #")),
        f("isSeasonal", "Seasonal merchant?", "yesno"),
        when((b) => b.isSeasonal === true, reqWhen((b) => b.isSeasonal === true, {
          key: "seasonalActiveMonths", label: "Months the business operates", type: "multicheck",
          options: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
        })),
        f("billsBeforeShipment", "Bill customers before goods shipped?", "yesno"),
        when((b) => b.billsBeforeShipment === true, sel("billBeforeShipmentDays", "Days before shipment", OPT.shipDays)),
        { key: "refundPolicy", label: "Return / Refund Policy", type: "textarea", required: true, maxLength: 100,
          presets: ["No Refund", "7 days refund", "14 days refund", "30 days refund"] },
        { key: "advertisingMethods", label: "How does the merchant advertise?", type: "multicheck",
          options: ["Yellow Pages", "Publications", "Internet", "Telemarketing", "Mass/Direct Mail", "Catalog", "Word of Mouth", "Other"] },
      ]),
      ...inSection("Credit card processing methods", [
        req(pct(f("pctCardSwiped", "Card-Swiped %", "number"))),
        req(pct(f("pctManuallyKeyedImprint", "Manually Keyed (Card Present with Imprint) %", "number"))),
        req(pct(f("pctManuallyKeyedNoImprint", "Manually Keyed (Card Not Present / MOTO) %", "number"))),
        req(pct(f("pctEcommerce", "eCommerce (Card Not Present) %", "number"))),
        when(
          (b) => typeof b.pctManuallyKeyedNoImprint === "number" && b.pctManuallyKeyedNoImprint > 20,
          f("usesThirdPartyFulfillment", "3rd-party Fulfillment", "yesno"),
        ),
        when((b) => b.usesThirdPartyFulfillment === true, mx(25, f("fulfillmentHouseName", "Fulfillment House Name"))),
        when((b) => b.usesThirdPartyFulfillment === true, f("fulfillmentHouseAddress", "Fulfillment House Address")),
        when((b) => b.usesThirdPartyFulfillment === true, withRule("phone", f("fulfillmentHousePhone", "Fulfillment House Phone"))),
        hint("Value must be 0 - 100%", req(pct(f("pctB2B", "Business-to-Business %", "number")))),
      ]),
      ...inSection("Projected volume", [
        req(f("avgTransactionAmount", "Average Transaction (Ticket) Amount $", "number")),
        f("maxTransactionAmount", "Largest Transaction $", "number"),
        req(f("monthlySalesTotal", "Total Monthly Sales $", "number")),
        req(hint("Drives ESA vs OptBlue eligibility.", f("amexVolumeOver1M", "Annual Amex Volume Exceeds $1M?", "yesno"))),
        f("amexMarketingOptIn", "Receive Amex Marketing Materials?", "yesno"),
      ]),
      ...inSection("Card acceptance", [
        radio(sel("cardTypesRequested", "Card Types Requested", OPT.cardTypes)),
        sel("qualifiedProcessingMethod", "Qualified Processing Method", OPT.procMethod),
        hint("PCI disclosure. List names + roles, or leave empty if none.",
          mx(255, f("contractorsWithCardData", "Independent Contractors with Cardholder Data Access", "textarea"))),
      ]),
    ],
  },
  {
    n: 5,
    label: "Banking",
    banner: "At minimum, one Deposit account. Add a separate Withdraw account when the merchant wants fees pulled from a different bank.",
    repeat: {
      collection: "bankAccounts",
      title: "Bank account",
      noun: "bank account",
      minRows: 1,
      rowFields: [
        radio(req(sel("purpose", "Purpose", OPT.bankPurpose))),
        radio(req(sel("accountType", "Account Type", OPT.accountType))),
        withRule("routing", hint("9 digits.", ph("123456789", req(mx(FIELD_MAX.appBankAccount.routingNumber, f("routingNumber", "Routing Number")))))),
        ph("Auto-lookup from routing #", mx(FIELD_MAX.appBankAccount.bankName, f("bankName", "Bank Name"))),
        withRule("accountNumber", req(mx(FIELD_MAX.appBankAccount.accountNumber, f("accountNumber", "Account Number")))),
        withRule("accountMatch", req(mx(FIELD_MAX.appBankAccount.accountNumber, f("reAccountNumber", "Re-enter Account Number")))),
        mx(FIELD_MAX.appBankAccount.bankContact, f("bankContact", "Bank Contact")),
        withRule("phone", mx(FIELD_MAX.appBankAccount.bankPhone, f("bankPhone", "Bank Phone"))),
        f("dateOpened", "Date Opened", "date"),
        f("isDefaultAccount", "Default account (used for all purposes)", "checkbox"),
        f("useDailySettlement", "Use for: daily settlement", "checkbox"),
        f("useMonthlyBilling", "Use for: monthly billing", "checkbox"),
        f("useChargebacks", "Use for: chargebacks", "checkbox"),
        f("useAchSettlement", "Use for: TXP ACH settlement", "checkbox"),
        f("useAchFees", "Use for: TXP ACH fees", "checkbox"),
      ],
    },
  },
  {
    n: 6,
    label: "Trade Refs",
    hidden: true,
    repeat: {
      collection: "tradeReferences",
      title: "Trade Reference",
      rowFields: [
        mx(FIELD_MAX.appTradeReference.tradeName, f("tradeName", "Trade Name")),
        mx(FIELD_MAX.appTradeReference.accountNumber, f("accountNumber", "Account Number")),
        withRule("phone", mx(FIELD_MAX.appTradeReference.phone, f("phone", "Phone"))),
        mx(FIELD_MAX.appTradeReference.productSold, f("productSold", "Product Sold")),
      ],
    },
  },
  {
    n: 7,
    label: "Fees",
    hidden: true,
    feeFields: [
      ...inSection("Pricing plan", [
        sel("processingType", "Processing Type", OPT.processingType),
        sel("pricingPlan", "Pricing Plan", OPT.pricingPlan),
        mx(FIELD_MAX.appWizard.pricingPlanOption, f("pricingPlanOption", "Pricing Plan Option")),
      ]),
      ...inSection("Base rates", [
        f("baseRatePct", "Base Rate %", "number"),
        f("baseRatePerItem", "Base Rate Per Item", "number"),
        when((b) => b.pricingPlan === "Differential", f("differentialPct", "Differential %", "number")),
        when((b) => b.pricingPlan === "Differential", f("differentialPerItem", "Differential Per Item", "number")),
        when((b) => b.pricingPlan === "Interchange Plus", f("plusNonQualPct", "Plus Non-Qual %", "number")),
        when((b) => b.pricingPlan === "Surcharge", f("checkCardRebatePct", "Check Card Rebate %", "number")),
        when((b) => b.pricingPlan === "Surcharge", f("checkCardRebatePerItem", "Check Card Rebate Per Item", "number")),
        when((b) => b.pricingPlan === "Surcharge" && b.processingType === "Retail", f("midQualPct", "Mid Qual %", "number")),
        when((b) => b.pricingPlan === "Surcharge" && b.processingType === "Retail", f("midQualPerItem", "Mid Qual Per Item", "number")),
        when((b) => b.pricingPlan === "Surcharge", f("nonQualPct", "Non Qual %", "number")),
        when((b) => b.pricingPlan === "Surcharge", f("nonQualPerItem", "Non Qual Per Item", "number")),
      ]),
      ...inSection("Interchange program", [
        f("discoverInterchangeProgram", "Discover Interchange Program", "yesno"),
        f("cardBrandServicesPct", "Card Brand Services %", "number"),
        f("cardBrandServicesPerItem", "Card Brand Services Per Item", "number"),
      ]),
      ...inSection("Interchange detail (optional)", [
        f("icSignatureDebitPct", "Signature Debit %", "number"),
        f("icSignatureDebitPerItem", "Signature Debit Per Item", "number"),
        f("icQualifiedPct", "Qualified %", "number"),
        f("icQualifiedPerItem", "Qualified Per Item", "number"),
        f("icRewardsPct", "Rewards %", "number"),
        f("icRewardsPerItem", "Rewards Per Item", "number"),
        f("icMidQualifiedPct", "Mid-Qualified %", "number"),
        f("icMidQualifiedPerItem", "Mid-Qualified Per Item", "number"),
        f("icNonQualifiedPct", "Non-Qualified %", "number"),
        f("icNonQualifiedPerItem", "Non-Qualified Per Item", "number"),
      ]),
      ...inSection("Authorization fees", [
        f("authPerCardType", "Auth Per Card Type", "number"),
        f("authVoice", "Auth Voice", "number"),
        f("authARU", "Auth ARU", "number"),
        f("batchCloseFee", "Batch Close Fee", "number"),
      ]),
      ...inSection("PIN Debit / EBT", [
        mx(FIELD_MAX.appWizard.pinDebitPlan, f("pinDebitPlan", "PIN Debit Plan")),
        f("pinDebitRate", "PIN Debit Rate %", "number"),
        f("pinDebitPerItem", "PIN Debit Per Item", "number"),
        f("pinDebitMonthly", "PIN Debit Monthly", "number"),
        f("pinDebitAppFee", "PIN Debit App Fee", "number"),
        f("ebtPerItem", "EBT Per Item", "number"),
        f("includeDailyDiscount", "Include Daily Discount", "yesno"),
        f("sameDayAch", "Same-Day ACH", "yesno"),
        f("sameDayAchFee", "Same-Day ACH Fee", "number"),
      ]),
      ...inSection("Security / PCI", [
        f("pciMonthly", "PCI Monthly", "number"),
        f("pciMonthlyStartMonth", "PCI Monthly Start Month", "date"),
        f("pciAnnual", "PCI Annual", "number"),
        f("pciQuarterlyNonValidation", "PCI Quarterly Non-Validation", "number"),
        f("pciQuarterlySupport", "PCI Quarterly Support", "number"),
        f("pciNonReceipt", "PCI Non-Receipt", "number"),
        f("dataProtectionPerItem", "Data Protection Per Item", "number"),
        f("p2pePerItem", "P2PE Per Item", "number"),
        f("breachCoverageFee", "Breach Coverage Fee", "number"),
        f("nonEmvFee", "Non-EMV Risk Assessment Fee %", "number"),
      ]),
      ...inSection("ACH (TC Plus)", [
        f("achDiscountRate", "ACH Discount Rate %", "number"),
        f("achTransFee", "ACH Transaction Fee", "number"),
        f("fraudCheckFee", "Fraud Check Fee", "number"),
      ]),
      ...inSection("Miscellaneous fees", [
        f("annualFee", "Annual Fee", "number"),
        f("monthlyServiceFee", "Monthly Service Fee", "number"),
        f("paperStatementFee", "Paper Statement Fee", "number"),
        f("appSetupFee", "App Setup Fee", "number"),
        f("chargebackFee", "Chargeback Fee", "number"),
        f("retrievalFee", "Retrieval Fee", "number"),
        f("monthlyMinimum", "Monthly Minimum", "number"),
        f("achReturnFee", "ACH Return Fee", "number"),
        f("achChangeFee", "ACH Change Fee", "number"),
        f("merchantClubFee", "Merchant Club Fee", "number"),
        f("trainingFee", "Training Fee", "number"),
        f("paymentGatewayMonthly", "Payment Gateway Monthly", "number"),
        f("paymentGatewaySetup", "Payment Gateway Setup", "number"),
        f("administrativeFee", "Administrative Fee", "number"),
      ]),
      ...inSection("Resellers", tight([
        f("includeWireless", "Include Wireless", "checkbox"),
        f("includeAuthNet", "Include Authorize.Net", "checkbox"),
        when((b) => b.includeAuthNet === true, f("authNetMonthly", "Auth.Net Monthly", "number")),
        when((b) => b.includeAuthNet === true, f("authNetSetup", "Auth.Net Setup", "number")),
        f("includeUnsupportedPos", "Include Unsupported POS", "checkbox"),
        f("includePetro", "Include Petro", "checkbox"),
        f("includeSmartLink", "Include SmartLink", "checkbox"),
      ])),
      ...inSection("Early termination waivers", tight([
        f("etfDoesNotApply", "Early Termination Fee does NOT apply", "checkbox"),
      ])),
    ],
  },
  {
    n: 8,
    label: "POS",
    hidden: true,
    fields: [
      ...inSection("Processing card summary", [
        { key: "processingCardSummary", label: "Payment types accepted beyond plain credit-card processing", type: "multicheck",
          options: ["Debit", "Debit/EBT Cash Back", "Check Service", "EBT", "Gift Card", "WEX", "Voyager"] },
      ]),
      ...inSection("Logistics", [
        req(sel("merchantTrainedBy", "Merchant Trained By", OPT.trainedBy)),
        req(sel("equipmentShippedTo", "Equipment Shipped To", OPT.shipTo)),
        sel("welcomeKitSentBy", "Welcome Kit Sent By", OPT.trainedBy),
        req(sel("welcomeKitShippedTo", "Welcome Kit Shipped To", OPT.shipTo)),
      ]),
    ],
    repeat: {
      collection: "posSetup",
      title: "TransIT package",
      noun: "TransIT package",
      rowFields: [
        mx(FIELD_MAX.appPosSetup.network, f("network", "Network")),
        mx(FIELD_MAX.appPosSetup.posBrand, f("posBrand", "POS Brand")),
        mx(FIELD_MAX.appPosSetup.posModel, f("posModel", "POS Model")),
        mx(FIELD_MAX.appPosSetup.printerBrand, f("printerBrand", "Printer Brand")),
        mx(FIELD_MAX.appPosSetup.printerModel, f("printerModel", "Printer Model")),
        sel("industry", "Industry", OPT.posIndustry),
        mx(FIELD_MAX.appPosSetup.applicationName, f("applicationName", "Application Name")),
        f("integrationRequired", "Integration Required", "yesno"),
        mx(FIELD_MAX.appPosSetup.integrationProductName, f("integrationProductName", "Integration Product")),
        whole(f("integratedProductId", "Integrated Product ID", "number")),
        whole(f("productCode", "Product Code", "number")),
        whole(f("terminalId", "Terminal ID", "number")),
        mx(FIELD_MAX.appPosSetup.packageOption, f("packageOption", "Package Option")),
        mx(FIELD_MAX.appPosSetup.packageType, f("packageType", "Package Type")),
        sel("batchCloseMethod", "Batch Close Method", OPT.batchClose),
        mx(FIELD_MAX.appWizard.batchCloseLocalTime, f("batchCloseLocalTime", "Batch Close Local Time")),
        sel("locationType", "Location Type", OPT.locationType),
        f("tokenization", "Tokenization", "yesno"),
        nmax(10, whole(f("qty", "Qty", "number"))),
        sel("code", "Equipment Code", OPT.equipmentCode),
        f("price", "Price", "number"),
        sel("billTo", "Bill To", OPT.billTo),
      ],
    },
  },
  {
    n: 9,
    label: "Site + Patriot",
    hidden: true,
    fields: [
      ...inSection("Merchant site survey", [
        req(radio(sel("siteVerificationOption", "Site verification", OPT.siteOption))),
        req(f("inventoryConsistentWithSignage", "Physical inventory consistent with business signage?", "yesno")),
        req(f("siteConsistentWithApplication", "Site consistent with application?", "yesno")),
      ]),
      ...inSection("Patriot Act", [
        hint("Most merchants use Section II (business). Section I is government entities; Section III is an individual.",
          req(radio(sel("patriotActSection", "Section", OPT.patriotSection)))),

        when(secEq("I"), reqWhen(secEq("I"), sel("patriotGovtDoc", "Supporting document", OPT.patriotGovtDoc))),
        when(govtDoc("GovtThirdParty"), reqWhen(govtDoc("GovtThirdParty"), mx(50, f("govtThirdPartyDescription", "Third-party verification — describe")))),

        when(secEq("II"), reqWhen(secEq("II"), sel("patriotBusinessDoc", "Supporting document", OPT.patriotBusinessDoc))),
        when(bizDoc("BusinessLicense"), reqWhen(bizDoc("BusinessLicense"), mx(50, f("blName", "Business License Name")))),
        when(bizDoc("BusinessLicense"), reqWhen(bizDoc("BusinessLicense"), sel("blState", "State of Issuance", STATE_OPTS))),
        when(bizDoc("BusinessLicense"), reqWhen(bizDoc("BusinessLicense"), f("blIssueDate", "Date of Issuance", "date"))),
        when(bizDoc("BusinessLicense"), reqWhen(bizDoc("BusinessLicense"), f("blExpireDate", "Expiration Date", "date"))),
        when(bizDoc("TaxReturn"), reqWhen(bizDoc("TaxReturn"), mx(25, f("taxReturnEmployerId", "IRS Employer ID")))),
        when(bizDoc("TaxReturn"), reqWhen(bizDoc("TaxReturn"), mx(25, f("taxReturnPlace", "Place of Issuance")))),
        when(bizDoc("TaxReturn"), reqWhen(bizDoc("TaxReturn"), f("taxReturnDateFiled", "Date Filed", "date"))),
        when(bizDoc("ArticlesOfIncorporation"), reqWhen(bizDoc("ArticlesOfIncorporation"), mx(25, f("aoiPlace", "Place of Issuance")))),
        when(bizDoc("ArticlesOfIncorporation"), reqWhen(bizDoc("ArticlesOfIncorporation"), f("aoiDateFiled", "Date Filed", "date"))),
        when(bizDoc("FinancialStatements"), reqWhen(bizDoc("FinancialStatements"), f("financialStatementsDate", "Statements Date", "date"))),

        when(secEq("III"), reqWhen(secEq("III"), sel("patriotIndividualDoc", "ID type", OPT.patriotIndividualDoc))),
        when(indDoc("StateID"), reqWhen(indDoc("StateID"), mx(25, f("stateIdNumber", "State ID Number")))),
        when(indDoc("StateID"), reqWhen(indDoc("StateID"), mx(25, f("stateIdIssuePlace", "Issue Place")))),
        when(indDoc("StateID"), reqWhen(indDoc("StateID"), f("stateIdIssueDate", "Issue Date", "date"))),
        when(indDoc("StateID"), reqWhen(indDoc("StateID"), f("stateIdExpireDate", "Expiration Date", "date"))),
        when(indDoc("Passport"), reqWhen(indDoc("Passport"), mx(25, f("patriotPassportNumber", "Passport Number")))),
        when(indDoc("Passport"), reqWhen(indDoc("Passport"), mx(50, f("patriotPassportCountry", "Issue Country")))),
        when(indDoc("Passport"), reqWhen(indDoc("Passport"), f("patriotPassportIssueDate", "Issue Date", "date"))),
        when(indDoc("Passport"), reqWhen(indDoc("Passport"), f("patriotPassportExpireDate", "Expiration Date", "date"))),
        when(indDoc("MilitaryID"), reqWhen(indDoc("MilitaryID"), withRule("ssn", f("militaryIdSsn", "Military ID SSN")))),
        when(indDoc("MilitaryID"), reqWhen(indDoc("MilitaryID"), mx(25, f("militaryIdIssuePlace", "Issue Place")))),
        when(indDoc("MilitaryID"), reqWhen(indDoc("MilitaryID"), f("militaryIdIssueDate", "Issue Date", "date"))),
        when(indDoc("MilitaryID"), reqWhen(indDoc("MilitaryID"), f("militaryIdExpireDate", "Expiration Date", "date"))),
      ]),
    ],
  },
  {
    n: 10,
    label: "Documents",
    fields: [
      req(f("hasAcceptedCardsBefore", "Accepted credit cards before?", "yesno")),
      when((b) => b.hasAcceptedCardsBefore === true,
        reqWhen((b) => b.hasAcceptedCardsBefore === true, mx(25, f("previousProcessorName", "Previous Processor Name")))),
    ],
    documents: true,
  },
  {
    n: 11,
    label: "Review",
    hideEsign: true,
    fields: [
      req(mx(FIELD_MAX.default.name, f("signerGuarantorName", "Guarantor Name"))),
      withRule("email", req(mx(FIELD_MAX.default.email, f("signerGuarantorEmail", "Guarantor Email")))),
      req(mx(FIELD_MAX.default.name, f("signerPrincipalName", "Principal Name"))),
      withRule("email", req(mx(FIELD_MAX.default.email, f("signerPrincipalEmail", "Principal Email")))),
    ],
    review: true,
  },
];

export const PRICING_FIELDS: FieldDef[] = [
  req(sel("pricingProgram", "Pricing program", [...PRICING_PROGRAMS])),
  floorOf((b) => (typeof b.pricingProgram === "string" ? PRICING_RATE_FLOORS[b.pricingProgram] : undefined),
    req(nmax(PRICING_MAX_RATE_PCT, f("pricingRatePct", "Rate %", "number")))),
  floorOf((b) => (program("Interchange Plus")(b) ? IC_PER_TXN_FLOOR : undefined),
    when(program("Interchange Plus", "Flat Rate"), reqWhen(program("Interchange Plus"), nmax(PRICING_MAX_FEE, f("pricingPerTxnFee", "Per transaction $", "number"))))),
  f("amexCustomRate", "Price American Express separately", "yesno"),
  checkOf((v, b) => (typeof v === "number" && typeof b.pricingRatePct === "number" && v <= b.pricingRatePct
    ? AMEX_NOT_HIGHER_MESSAGE : null), pctFor(amexCustom, "amexRatePct", "Amex rate %")),
  f("pinDebitEnabled", "Enable PIN debit", "yesno"),
  pctFor(pinDebitOn, "pinDebitRatePct", "PIN debit rate %"),
  usdFor(pinDebitOn, "pinDebitPerItemFee", "PIN debit per item fee $"),
  f("onlineOrdering", "Online ordering", "yesno"),
  f("sensaInvoices", "SensaInvoices", "yesno"),
  f("acceptAch", "Accept ACH", "yesno"),
  when(acceptsAch, reqWhen(achMissing("achFeePerTxn"), nmax(PRICING_MAX_RATE_PCT, f("achRatePct", "ACH rate %", "number")))),
  when(acceptsAch, reqWhen(achMissing("achRatePct"), nmax(PRICING_MAX_FEE, f("achFeePerTxn", "ACH fee per transaction $", "number")))),
  ph("Anything back office should know about this merchant's pricing", mx(500,
    f("pricingNotesForBackOffice", "Notes for back office (optional)", "textarea"))),
];

export const PRICING_DEPENDENTS: Record<string, string[]> = {
  pricingProgram: ["pricingRatePct", "pricingPerTxnFee"],
  pricingRatePct: ["amexRatePct"],
  achRatePct: ["achFeePerTxn"],
  achFeePerTxn: ["achRatePct"],
};

export const VISIBLE_STEPS = STEPS.filter((s) => !s.hidden);
export const VISIBLE_STEP_COUNT = VISIBLE_STEPS.length;

export function visibleStepPosition(n: number): number {
  const idx = VISIBLE_STEPS.findIndex((s) => s.n === n);
  if (idx >= 0) return idx + 1;
  const below = VISIBLE_STEPS.filter((s) => s.n <= n).length;
  return below > 0 ? below : 1;
}
